import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATIC_IMAGES = {
  logoDark: "/assets/logo-dark.png",
  logoLight: "/assets/logo-light.png",
  cover: "/assets/from-campus-to-corporate-cover.png",
  scott: "/assets/scott-tang.png",
  icon: "/assets/icon.png",
};
const IMAGE_LABELS = {
  logoDark: "Logo (dark background)",
  logoLight: "Logo (light background)",
  cover: "eBook cover",
  scott: "Scott's photo",
  icon: "App icon (mobile bar)",
};
const detail = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong."; };
const clone = (o) => JSON.parse(JSON.stringify(o));

const setIn = (obj, path, value) => {
  const next = clone(obj);
  let cur = next;
  for (let i = 0; i < path.length - 1; i += 1) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
  return next;
};

const Field = ({ label, value, onChange, testId, placeholder, type = "text" }) => (
  <label className="cms-field" data-testid={testId ? `${testId}-wrap` : undefined}>
    <span>{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </label>
);
const Area = ({ label, value, onChange, rows = 3, testId }) => (
  <label className="cms-field">
    <span>{label}</span>
    <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </label>
);

const SUBTABS = [
  ["general", "General"], ["hero", "Hero"], ["fiveps", "The 5Ps"], ["offer", "eBook Page"], ["testimonials", "Testimonials"],
  ["sample", "Free lesson"], ["scott", "About Scott"], ["brandstory", "Brand story"], ["faqs", "FAQs"], ["lessons", "Lessons"], ["images", "Images"], ["ebook", "eBook File"],
  ["comingsoon", "Coming Soon page"], ["payments", "Payments"],
];

const ImageSlot = ({ slot, url, onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = url ? `${BACKEND}${url}` : STATIC_IMAGES[slot];
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { const res = await adminApi.uploadImage(slot, file); toast.success("Image updated"); onUploaded(slot, res.url); }
    catch (err) { toast.error(detail(err)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const reset = async () => {
    try { await adminApi.deleteImage(slot); toast.success("Reverted to default image"); onUploaded(slot, null); }
    catch (err) { toast.error(detail(err)); }
  };
  return (
    <div className="cms-image-slot" data-testid={`cms-image-${slot}`}>
      <div className="cms-image-preview"><img src={preview} alt={IMAGE_LABELS[slot]} /></div>
      <div className="cms-image-meta">
        <strong>{IMAGE_LABELS[slot]}</strong>
        <small>{url ? "Custom upload active" : "Using default"}</small>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pick} data-testid={`cms-image-input-${slot}`} />
        <div className="cms-image-actions">
          <button className="btn btn-gold" type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`cms-image-upload-${slot}`}>{busy ? "Uploading..." : "Upload new"}</button>
          {url && <button className="admin-ghost" type="button" onClick={reset} data-testid={`cms-image-reset-${slot}`}>Reset to default</button>}
        </div>
      </div>
    </div>
  );
};

export const ContentManager = ({ onUnauthorized, initialTab = "general" }) => {
  const [draft, setDraft] = useState(null);
  const [images, setImages] = useState({});
  const [ebook, setEbook] = useState({});
  const [reward, setReward] = useState({});
  const [notifyEmail, setNotifyEmail] = useState("");
  const [stripe, setStripe] = useState(null);
  const [stripeForm, setStripeForm] = useState({ publishable_key: "", secret_key: "", webhook_secret: "" });
  const [stripeTest, setStripeTest] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const pdfRef = useRef(null);
  const rewardRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getContent();
      setDraft(data.content); setImages(data.images || {}); setEbook(data.ebook || {}); setReward(data.rewardSheet || {}); setNotifyEmail(data.notifyEmail || "");
      try { const st = await adminApi.getStripe(); setStripe(st); setStripeForm((f) => ({ ...f, publishable_key: st.publishable_key || "" })); } catch (e) { console.debug("Stripe settings unavailable (non-fatal)", e?.message); }
    } catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const upd = (path, value) => setDraft((d) => setIn(d, path, value));

  const saveContent = async () => {
    setSaving(true);
    try { await adminApi.saveContent(draft); toast.success("Website content saved. Your site is updated."); }
    catch (err) { toast.error(detail(err)); }
    finally { setSaving(false); }
  };
  const saveEmail = async () => {
    try { await adminApi.saveSettings(notifyEmail.trim()); toast.success("Notification email saved"); }
    catch (err) { toast.error(detail(err)); }
  };
  const onImageUploaded = (slot, url) => setImages((im) => ({ ...im, [slot]: url }));
  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const res = await adminApi.uploadEbook(file); setEbook({ filename: res.filename, size: res.size }); toast.success("eBook PDF uploaded. Buyers now receive this file."); }
    catch (err) { toast.error(detail(err)); }
    finally { if (pdfRef.current) pdfRef.current.value = ""; }
  };
  const removePdf = async () => {
    try { await adminApi.deleteEbook(); setEbook({}); toast.success("Custom eBook removed. The placeholder will be used."); }
    catch (err) { toast.error(detail(err)); }
  };
  const uploadReward = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const res = await adminApi.uploadReward(file); setReward({ filename: res.filename, size: res.size }); toast.success("Reflection sheet uploaded. 4-5★ reviewers now receive it."); }
    catch (err) { toast.error(detail(err)); }
    finally { if (rewardRef.current) rewardRef.current.value = ""; }
  };
  const removeReward = async () => {
    try { await adminApi.deleteReward(); setReward({}); toast.success("Reflection sheet removed. No thank-you gift will be sent."); }
    catch (err) { toast.error(detail(err)); }
  };
  const saveStripe = async () => {
    setSaving(true);
    try {
      const payload = { publishable_key: stripeForm.publishable_key.trim() };
      if (stripeForm.secret_key.trim()) payload.secret_key = stripeForm.secret_key.trim();
      if (stripeForm.webhook_secret.trim()) payload.webhook_secret = stripeForm.webhook_secret.trim();
      const st = await adminApi.saveStripe(payload);
      setStripe(st); setStripeForm((f) => ({ ...f, secret_key: "", webhook_secret: "" }));
      toast.success("Payment keys saved securely.");
    } catch (err) { toast.error(detail(err)); }
    finally { setSaving(false); }
  };
  const testStripe = async () => {
    setStripeTest({ loading: true });
    try {
      const r = await adminApi.testStripe();
      setStripeTest(r);
      const st = await adminApi.getStripe(); setStripe(st);
      toast.success(`Connected to Stripe (${r.mode} mode). Product ${r.product_ready ? "ready" : "missing"}.`);
    } catch (err) { setStripeTest({ error: detail(err) }); toast.error(detail(err)); }
  };
  const clearStripe = async () => {
    try { const st = await adminApi.clearStripe(); setStripe(st); setStripeForm({ publishable_key: st.publishable_key || "", secret_key: "", webhook_secret: "" }); toast.success("Dashboard keys cleared."); }
    catch (err) { toast.error(detail(err)); }
  };

  if (loading || !draft) return <div className="admin-empty" data-testid="cms-loading">Loading website content...</div>;

  const fp = draft.fivePs;
  return (
    <div className="cms" data-testid="cms-panel">
      <div className="cms-layout">
        <aside className="cms-nav" data-testid="cms-nav">
          {SUBTABS.map(([id, label]) => (
            <button key={id} className={`cms-nav-btn${tab === id ? " active" : ""}`} onClick={() => setTab(id)} data-testid={`cms-tab-${id}`}>{label}</button>
          ))}
        </aside>

        <div className="cms-body">
          {tab === "general" && (
            <div className="cms-section" data-testid="cms-general">
              <h3>General</h3>
              <Field label="eBook price label" value={draft.price} onChange={(v) => upd(["price"], v)} testId="cms-price" placeholder="S$6.99" />
              <Field label="Booking button label" value={draft.bookingCta} onChange={(v) => upd(["bookingCta"], v)} testId="cms-booking-cta" placeholder="Book a 5Ps Conversation" />
              <Field label="TikTok URL" value={draft.links?.tiktok} onChange={(v) => upd(["links", "tiktok"], v)} testId="cms-tiktok" />
              <Field label="LinkedIn URL" value={draft.links?.linkedin} onChange={(v) => upd(["links", "linkedin"], v)} testId="cms-linkedin" />
              <Area label="Booking time slots (one per line)" rows={4} value={(draft.timeSlots || []).join("\n")} onChange={(v) => upd(["timeSlots"], v.split("\n").map((s) => s.trim()).filter(Boolean))} testId="cms-timeslots" />
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save">{saving ? "Saving..." : "Save content"}</button></div>
              <hr className="cms-divider" />
              <h3>Booking &amp; purchase alert email</h3>
              <p className="cms-hint">New bookings and support requests are emailed to this address.</p>
              <Field label="Notification email" value={notifyEmail} onChange={setNotifyEmail} testId="cms-notify-email" placeholder="scott@thetransitionworks.com" />
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveEmail} data-testid="cms-save-email">Save email</button></div>
            </div>
          )}

          {tab === "hero" && (
            <div className="cms-section" data-testid="cms-hero">
              <h3>Hero section</h3>
              <Field label="Kicker" value={draft.hero.kicker} onChange={(v) => upd(["hero", "kicker"], v)} />
              <Field label="Eyebrow" value={draft.hero.eyebrow} onChange={(v) => upd(["hero", "eyebrow"], v)} />
              <Field label="Title line 1" value={draft.hero.titleLine1} onChange={(v) => upd(["hero", "titleLine1"], v)} testId="cms-hero-title1" />
              <Field label="Title line 2 (gold)" value={draft.hero.titleLine2} onChange={(v) => upd(["hero", "titleLine2"], v)} />
              <Area label="Lead paragraph" value={draft.hero.lead} onChange={(v) => upd(["hero", "lead"], v)} />
              <Field label="Byline" value={draft.hero.byline} onChange={(v) => upd(["hero", "byline"], v)} />
              <Field label="Primary button" value={draft.hero.primaryCta} onChange={(v) => upd(["hero", "primaryCta"], v)} />
              <Field label="Secondary button" value={draft.hero.secondaryCta} onChange={(v) => upd(["hero", "secondaryCta"], v)} />
              <Area label="Microcopy" value={draft.hero.microcopy} onChange={(v) => upd(["hero", "microcopy"], v)} />
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-hero">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "fiveps" && (
            <div className="cms-section" data-testid="cms-fiveps">
              <h3>The 5Ps</h3>
              <Field label="Eyebrow" value={fp.eyebrow} onChange={(v) => upd(["fivePs", "eyebrow"], v)} />
              <Field label="Heading line 1" value={fp.headingTop} onChange={(v) => upd(["fivePs", "headingTop"], v)} />
              <Field label="Heading line 2" value={fp.headingBottom} onChange={(v) => upd(["fivePs", "headingBottom"], v)} />
              <Area label="Lead" value={fp.lead} onChange={(v) => upd(["fivePs", "lead"], v)} />
              {fp.order.map((k) => (
                <div className="cms-card" key={k} data-testid={`cms-p-${k}`}>
                  <div className="cms-card-title">{fp.items[k].name}</div>
                  <Field label="Name" value={fp.items[k].name} onChange={(v) => upd(["fivePs", "items", k, "name"], v)} />
                  <Field label="Question" value={fp.items[k].q} onChange={(v) => upd(["fivePs", "items", k, "q"], v)} />
                  <Area label="Description" value={fp.items[k].text} onChange={(v) => upd(["fivePs", "items", k, "text"], v)} />
                  <Field label="Tags (comma separated)" value={(fp.items[k].tags || []).join(", ")} onChange={(v) => upd(["fivePs", "items", k, "tags"], v.split(",").map((s) => s.trim()).filter(Boolean))} />
                </div>
              ))}
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-fiveps">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "offer" && (
            <div className="cms-section" data-testid="cms-offer">
              <h3>eBook page (offer)</h3>
              <Field label="Eyebrow" value={draft.offer.eyebrow} onChange={(v) => upd(["offer", "eyebrow"], v)} />
              <Field label="Heading start" value={draft.offer.headingPre} onChange={(v) => upd(["offer", "headingPre"], v)} />
              <Field label="Heading highlight" value={draft.offer.headingHighlight} onChange={(v) => upd(["offer", "headingHighlight"], v)} />
              <Field label="Heading end" value={draft.offer.headingPost} onChange={(v) => upd(["offer", "headingPost"], v)} />
              <Area label="Lead" value={draft.offer.lead} onChange={(v) => upd(["offer", "lead"], v)} />
              <Area label="Benefits (one per line)" rows={5} value={(draft.offer.benefits || []).join("\n")} onChange={(v) => upd(["offer", "benefits"], v.split("\n").map((s) => s.trim()).filter(Boolean))} />
              <Field label="Value note" value={draft.offer.valueNote} onChange={(v) => upd(["offer", "valueNote"], v)} />
              <Field label="Included badge (1:1 with Scott)" value={draft.offer.includeBadge} onChange={(v) => upd(["offer", "includeBadge"], v)} testId="cms-include-badge" />
              <Field label="Price kicker" value={draft.offer.priceKicker} onChange={(v) => upd(["offer", "priceKicker"], v)} />
              <Field label="Price subtext" value={draft.offer.priceSub} onChange={(v) => upd(["offer", "priceSub"], v)} />
              <Field label="Trust line (under the buy button)" value={draft.offer.trustLine} onChange={(v) => upd(["offer", "trustLine"], v)} testId="cms-trust-line" />
              <Field label="Primary button" value={draft.offer.primaryCta} onChange={(v) => upd(["offer", "primaryCta"], v)} />
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-offer">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "testimonials" && (
            <div className="cms-section" data-testid="cms-testimonials">
              <h3>Testimonials</h3>
              <p className="cms-hint">The site ships with clearly marked sample quotes. Replace them with real reader feedback before going live. Remove all of them to hide the section.</p>
              <Field label="Eyebrow" value={draft.testimonials.eyebrow} onChange={(v) => upd(["testimonials", "eyebrow"], v)} />
              <Field label="Heading" value={draft.testimonials.heading} onChange={(v) => upd(["testimonials", "heading"], v)} />
              {draft.testimonials.items.map((t, i) => (
                <div className="cms-card" key={i} data-testid={`cms-testimonial-${i}`}>
                  <div className="cms-card-head"><span>Quote {i + 1}</span>
                    <button className="cms-remove" onClick={() => upd(["testimonials", "items"], draft.testimonials.items.filter((_, j) => j !== i))} data-testid={`cms-testimonial-remove-${i}`}>Remove</button>
                  </div>
                  <Area label="Quote" value={t.quote} onChange={(v) => upd(["testimonials", "items", i, "quote"], v)} testId={`cms-testimonial-quote-${i}`} />
                  <div className="cms-row2">
                    <Field label="Name" value={t.name} onChange={(v) => upd(["testimonials", "items", i, "name"], v)} testId={`cms-testimonial-name-${i}`} />
                    <Field label="Role / context" value={t.role} onChange={(v) => upd(["testimonials", "items", i, "role"], v)} />
                  </div>
                </div>
              ))}
              <button className="cms-add" onClick={() => upd(["testimonials", "items"], [...draft.testimonials.items, { quote: "", name: "", role: "" }])} data-testid="cms-testimonial-add">+ Add testimonial</button>
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-testimonials">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "sample" && (
            <div className="cms-section" data-testid="cms-sample">
              <h3>Free lesson section</h3>
              <p className="cms-hint">Visitors leave their email and receive one lesson from the eBook. Sign-ups appear under the Leads tab.</p>
              <Field label="Eyebrow" value={draft.sample.eyebrow} onChange={(v) => upd(["sample", "eyebrow"], v)} />
              <Field label="Heading" value={draft.sample.heading} onChange={(v) => upd(["sample", "heading"], v)} testId="cms-sample-heading" />
              <Area label="Body" value={draft.sample.body} onChange={(v) => upd(["sample", "body"], v)} />
              <Field label="Button label" value={draft.sample.cta} onChange={(v) => upd(["sample", "cta"], v)} />
              <label className="cms-field"><span>Lesson to send</span>
                <select value={draft.sample.lessonTitle} onChange={(e) => upd(["sample", "lessonTitle"], e.target.value)} data-testid="cms-sample-lesson">
                  {draft.lessons.map((l) => <option key={l.title} value={l.title}>{`${l.category} · ${l.title}`}</option>)}
                </select>
              </label>
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-sample">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "comingsoon" && (
            <div className="cms-section" data-testid="cms-comingsoon">
              <h3>Coming Soon page</h3>
              <p className="cms-hint">Shown to the public while the website is in Coming Soon mode (Launch tab). Emails collected here appear under Leads.</p>
              <Field label="Kicker" value={draft.comingSoon.kicker} onChange={(v) => upd(["comingSoon", "kicker"], v)} />
              <Field label="Headline" value={draft.comingSoon.headline} onChange={(v) => upd(["comingSoon", "headline"], v)} testId="cms-cs-headline" />
              <Field label="Subline" value={draft.comingSoon.subline} onChange={(v) => upd(["comingSoon", "subline"], v)} />
              <Area label="Body" value={draft.comingSoon.body} onChange={(v) => upd(["comingSoon", "body"], v)} />
              <Field label="Button label" value={draft.comingSoon.cta} onChange={(v) => upd(["comingSoon", "cta"], v)} />
              <Field label="Thank-you message" value={draft.comingSoon.thanks} onChange={(v) => upd(["comingSoon", "thanks"], v)} />
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-comingsoon">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "scott" && (
            <div className="cms-section" data-testid="cms-scott">
              <h3>About Scott</h3>
              <Field label="Eyebrow" value={draft.scott.eyebrow} onChange={(v) => upd(["scott", "eyebrow"], v)} />
              <Area label="Heading" value={draft.scott.heading} onChange={(v) => upd(["scott", "heading"], v)} />
              <Area label="Lead" value={draft.scott.lead} onChange={(v) => upd(["scott", "lead"], v)} />
              {(draft.scott.paragraphs || []).map((para, i) => (
                <Area key={i} label={`Paragraph ${i + 1}`} rows={4} value={para} onChange={(v) => upd(["scott", "paragraphs", i], v)} />
              ))}
              <Field label="Sign-off name" value={draft.scott.signoffName} onChange={(v) => upd(["scott", "signoffName"], v)} />
              <Field label="Sign-off title" value={draft.scott.signoffTitle} onChange={(v) => upd(["scott", "signoffTitle"], v)} />
              <Field label="Sign-off subtitle" value={draft.scott.signoffSub} onChange={(v) => upd(["scott", "signoffSub"], v)} />
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-scott">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "brandstory" && (
            <div className="cms-section" data-testid="cms-brandstory">
              <h3>Brand story (how the name came about)</h3>
              <p className="cms-hint">Shown as a dark feature section on the homepage, after About Scott, explaining the meaning behind the names.</p>
              <Field label="Eyebrow" value={draft.brandStory?.eyebrow} onChange={(v) => upd(["brandStory", "eyebrow"], v)} testId="cms-brandstory-eyebrow" />
              <Field label="Heading" value={draft.brandStory?.heading} onChange={(v) => upd(["brandStory", "heading"], v)} testId="cms-brandstory-heading" />
              <Area label="Lead" value={draft.brandStory?.lead} onChange={(v) => upd(["brandStory", "lead"], v)} />
              {(draft.brandStory?.items || []).map((it, i) => (
                <div className="cms-card" key={i} data-testid={`cms-brandstory-item-${i}`}>
                  <div className="cms-card-head"><span>Name {i + 1}</span>
                    <button className="cms-remove" onClick={() => upd(["brandStory", "items"], draft.brandStory.items.filter((_, j) => j !== i))} data-testid={`cms-brandstory-remove-${i}`}>Remove</button>
                  </div>
                  <div className="cms-row2">
                    <Field label="Name / term" value={it.term} onChange={(v) => upd(["brandStory", "items", i, "term"], v)} testId={`cms-brandstory-term-${i}`} />
                    <Field label="Short meaning" value={it.meaning} onChange={(v) => upd(["brandStory", "items", i, "meaning"], v)} />
                  </div>
                  <Area label="Explanation" value={it.text} onChange={(v) => upd(["brandStory", "items", i, "text"], v)} testId={`cms-brandstory-text-${i}`} />
                </div>
              ))}
              <button className="cms-add" onClick={() => upd(["brandStory", "items"], [...(draft.brandStory?.items || []), { term: "", meaning: "", text: "" }])} data-testid="cms-brandstory-add">+ Add name</button>
              <div className="cms-row2">
                <Field label="Promise label" value={draft.brandStory?.promiseLabel} onChange={(v) => upd(["brandStory", "promiseLabel"], v)} />
                <Field label="Brand promise" value={draft.brandStory?.promise} onChange={(v) => upd(["brandStory", "promise"], v)} />
              </div>
              <div className="cms-row2">
                <Field label="Theme label" value={draft.brandStory?.themeLabel} onChange={(v) => upd(["brandStory", "themeLabel"], v)} />
                <Field label="Brand theme" value={draft.brandStory?.theme} onChange={(v) => upd(["brandStory", "theme"], v)} />
              </div>
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-brandstory">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "payments" && stripe && (
            <div className="cms-section" data-testid="cms-payments">
              <h3>Payments (Stripe keys)</h3>
              <p className="cms-hint">Paste your own Stripe keys to take real payments. Keys are stored encrypted and never shown again after saving. Dashboard keys override any environment keys.</p>

              <div className="cms-ebook-status" data-testid="cms-stripe-status">
                <div>
                  Status: {stripe.configured
                    ? <><strong style={{ color: stripe.mode === "live" ? "#0a7d3c" : "#a06a00" }}>{stripe.mode === "live" ? "LIVE" : stripe.mode === "test" ? "TEST" : "SET"}</strong> · secret ••••{stripe.secret_last4} · source: {stripe.source}{stripe.dashboard_managed ? " (dashboard)" : ""}</>
                    : <span className="admin-muted">No Stripe key configured yet — the buy button shows "payments are being set up".</span>}
                </div>
                <div style={{ marginTop: 6 }}>Webhook signing secret: {stripe.webhook_set ? <>set ••••{stripe.webhook_last4}</> : <span className="admin-muted">not set (payments still confirm via polling fallback)</span>}</div>
              </div>

              <Field label="Publishable key (pk_live_… or pk_test_…)" value={stripeForm.publishable_key} onChange={(v) => setStripeForm((f) => ({ ...f, publishable_key: v }))} placeholder="pk_live_…" testId="cms-stripe-publishable" />
              <Field label={`Secret key ${stripe.secret_set ? "(leave blank to keep current)" : "(sk_live_… or sk_test_…)"}`} type="password" value={stripeForm.secret_key} onChange={(v) => setStripeForm((f) => ({ ...f, secret_key: v }))} placeholder={stripe.secret_set ? "•••• saved — enter to replace" : "sk_live_…"} testId="cms-stripe-secret" />
              <Field label={`Webhook signing secret ${stripe.webhook_set ? "(leave blank to keep current)" : "(whsec_…)"}`} type="password" value={stripeForm.webhook_secret} onChange={(v) => setStripeForm((f) => ({ ...f, webhook_secret: v }))} placeholder={stripe.webhook_set ? "•••• saved — enter to replace" : "whsec_…"} testId="cms-stripe-webhook" />

              <div className="cms-save-row">
                <button className="btn btn-gold" onClick={saveStripe} disabled={saving} data-testid="cms-stripe-save">{saving ? "Saving..." : "Save keys"}</button>
                <button className="admin-ghost" onClick={testStripe} disabled={stripeTest?.loading} data-testid="cms-stripe-test">{stripeTest?.loading ? "Testing..." : "Test & sync product"}</button>
                {stripe.dashboard_managed && <button className="admin-ghost" onClick={clearStripe} data-testid="cms-stripe-clear">Clear dashboard keys</button>}
              </div>

              {stripeTest && !stripeTest.loading && (
                <div className="cms-ebook-status" data-testid="cms-stripe-test-result" style={{ marginTop: 14 }}>
                  {stripeTest.error
                    ? <span style={{ color: "#b3261e" }}>✕ {stripeTest.error}</span>
                    : <span style={{ color: "#0a7d3c" }}>✓ Connected — {stripeTest.mode?.toUpperCase()} mode · account {stripeTest.account_id} ({stripeTest.account_country}) · eBook product {stripeTest.product_ready ? "ready" : "NOT found"}</span>}
                </div>
              )}

              <p className="cms-hint" style={{ marginTop: 16 }}>
                Webhook endpoint for Stripe: add a webhook in your Stripe Dashboard (Developers → Webhooks) pointing to <code>{`${BACKEND}/api/stripe/webhook`}</code>, then paste its signing secret above.
              </p>
            </div>
          )}

          {tab === "faqs" && (
            <div className="cms-section" data-testid="cms-faqs">
              <h3>FAQs</h3>
              {draft.faqs.map((f, i) => (
                <div className="cms-card" key={i} data-testid={`cms-faq-${i}`}>
                  <div className="cms-card-head"><span>FAQ {i + 1}</span>
                    <button className="cms-remove" onClick={() => upd(["faqs"], draft.faqs.filter((_, j) => j !== i))} data-testid={`cms-faq-remove-${i}`}>Remove</button>
                  </div>
                  <Field label="Question" value={f.q} onChange={(v) => upd(["faqs", i, "q"], v)} />
                  <Area label="Answer" value={f.a} onChange={(v) => upd(["faqs", i, "a"], v)} />
                </div>
              ))}
              <button className="cms-add" onClick={() => upd(["faqs"], [...draft.faqs, { q: "New question", a: "New answer" }])} data-testid="cms-faq-add">+ Add FAQ</button>
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-faqs">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "lessons" && (
            <div className="cms-section" data-testid="cms-lessons">
              <h3>Lessons <span className="cms-count">{draft.lessons.length}</span></h3>
              <p className="cms-hint">Category should be one of: Purpose, People, Program, Place, Profit.</p>
              {draft.lessons.map((l, i) => (
                <div className="cms-card" key={i} data-testid={`cms-lesson-${i}`}>
                  <div className="cms-card-head"><span>Lesson {i + 1}</span>
                    <button className="cms-remove" onClick={() => upd(["lessons"], draft.lessons.filter((_, j) => j !== i))} data-testid={`cms-lesson-remove-${i}`}>Remove</button>
                  </div>
                  <div className="cms-row2">
                    <Field label="Category" value={l.category} onChange={(v) => upd(["lessons", i, "category"], v)} />
                    <Field label="Title" value={l.title} onChange={(v) => upd(["lessons", i, "title"], v)} />
                  </div>
                  <Area label="Text" value={l.text} onChange={(v) => upd(["lessons", i, "text"], v)} />
                  <Area label="Reflection question" value={l.question} onChange={(v) => upd(["lessons", i, "question"], v)} />
                </div>
              ))}
              <button className="cms-add" onClick={() => upd(["lessons"], [...draft.lessons, { category: "Purpose", title: "New lesson", text: "", question: "" }])} data-testid="cms-lesson-add">+ Add lesson</button>
              <div className="cms-save-row"><button className="btn btn-gold" onClick={saveContent} disabled={saving} data-testid="cms-save-lessons">{saving ? "Saving..." : "Save content"}</button></div>
            </div>
          )}

          {tab === "images" && (
            <div className="cms-section" data-testid="cms-images">
              <h3>Images</h3>
              <p className="cms-hint">Uploads replace the default images instantly across the site. Recommended: PNG or WEBP, under 6 MB.</p>
              {Object.keys(IMAGE_LABELS).map((slot) => (
                <ImageSlot key={slot} slot={slot} url={images[slot]} onUploaded={onImageUploaded} />
              ))}
            </div>
          )}

          {tab === "ebook" && (
            <div className="cms-section" data-testid="cms-ebook">
              <h3>eBook PDF</h3>
              <p className="cms-hint">Upload the final eBook PDF. Buyers receive this file after a successful payment. Max 30 MB.</p>
              <div className="cms-ebook-status" data-testid="cms-ebook-status">
                {ebook.filename ? <><strong>Current file:</strong> {ebook.filename}{ebook.size ? ` (${Math.round(ebook.size / 1024)} KB)` : ""}</> : <span className="admin-muted">No custom PDF uploaded. Buyers currently receive the branded placeholder.</span>}
              </div>
              <input ref={pdfRef} type="file" accept="application/pdf" onChange={uploadPdf} data-testid="cms-ebook-input" style={{ display: "none" }} />
              <div className="cms-save-row">
                <button className="btn btn-gold" onClick={() => pdfRef.current?.click()} data-testid="cms-ebook-upload">{ebook.filename ? "Replace PDF" : "Upload PDF"}</button>
                {ebook.filename && <button className="admin-ghost" onClick={removePdf} data-testid="cms-ebook-remove">Remove custom PDF</button>}
              </div>

              <h3 style={{ marginTop: 32 }}>Reviewer thank-you: 5Ps Reflection Sheet</h3>
              <p className="cms-hint">Optional bonus PDF automatically emailed to readers who leave a 4 or 5 star review, and offered as a download on their thank-you page. Max 30 MB.</p>
              <div className="cms-ebook-status" data-testid="cms-reward-status">
                {reward.filename ? <><strong>Current file:</strong> {reward.filename}{reward.size ? ` (${Math.round(reward.size / 1024)} KB)` : ""}</> : <span className="admin-muted">No reflection sheet uploaded. Reviewers will not receive a thank-you gift yet.</span>}
              </div>
              <input ref={rewardRef} type="file" accept="application/pdf" onChange={uploadReward} data-testid="cms-reward-input" style={{ display: "none" }} />
              <div className="cms-save-row">
                <button className="btn btn-gold" onClick={() => rewardRef.current?.click()} data-testid="cms-reward-upload">{reward.filename ? "Replace sheet" : "Upload sheet"}</button>
                {reward.filename && <button className="admin-ghost" onClick={removeReward} data-testid="cms-reward-remove">Remove sheet</button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
